import { useState, useRef, useCallback } from 'react';
import apiClient from '../lib/api-client';

/**
 * useFEMAReport
 *
 * Encapsulates the FEMA PDF report download lifecycle:
 *   - Single report: API call → open pre-signed URL in new tab
 *   - Multiple reports: sequential downloads with progress tracking
 *   - Toast notification state (success / error / progress)
 *   - Auto-dismiss timer management
 */
function useFEMAReport() {
  // ── State ──────────────────────────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState(null);   // 'success' | 'error' | 'progress' | null
  const [blobUrl, setBlobUrl] = useState(null);
  const [filename, setFilename] = useState(null);
  const [progress, setProgress] = useState(null); // { current, total, failed }

  // ── Refs ───────────────────────────────────────────────────────────────────
  const autoDismissTimerRef = useRef(null);

  // ── dismissToast ───────────────────────────────────────────────────────────
  const dismissToast = useCallback(() => {
    if (autoDismissTimerRef.current !== null) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }

    setToastVisible(false);
    setBlobUrl(null);
    setToastType(null);
    setProgress(null);
  }, []);

  // ── generate (single report) ──────────────────────────────────────────────
  const generate = useCallback(async (femaRegion, scenario) => {
    if (autoDismissTimerRef.current !== null) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
    setToastVisible(false);
    setToastType(null);
    setBlobUrl(null);
    setFilename(null);
    setProgress(null);

    setIsGenerating(true);

    try {
      const response = await apiClient.generateFEMAReport(femaRegion, scenario);
      const downloadUrl = response.url.trim();
      const reportName = response.report_name || `FEMA_Region_${femaRegion}_${scenario}_report.pdf`;

      setBlobUrl(downloadUrl);
      setFilename(reportName);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');

      setIsGenerating(false);
      setToastType('success');
      setToastVisible(true);

      autoDismissTimerRef.current = setTimeout(() => {
        dismissToast();
      }, 20_000);

    } catch (error) {
      console.error('FEMA report download failed:', error);
      setIsGenerating(false);
      setToastType('error');
      setToastVisible(true);

      autoDismissTimerRef.current = setTimeout(() => {
        dismissToast();
      }, 15_000);
    }
  }, [dismissToast]);

  // ── generateMultiple (sequential downloads) ───────────────────────────────
  const generateMultiple = useCallback(async (regionIds, scenario) => {
    if (autoDismissTimerRef.current !== null) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }

    setToastVisible(true);
    setToastType('progress');
    setBlobUrl(null);
    setFilename(null);
    setIsGenerating(true);

    const total = regionIds.length;
    let failed = 0;

    setProgress({ current: 0, total, failed: 0 });

    for (let i = 0; i < total; i++) {
      const regionId = regionIds[i];
      setProgress({ current: i + 1, total, failed });

      try {
        const response = await apiClient.generateFEMAReport(regionId, scenario);
        const downloadUrl = response.url.trim();

        // Trigger download without opening a new tab
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = '';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error(`FEMA report download failed for region ${regionId}:`, error);
        failed++;
        setProgress({ current: i + 1, total, failed });
      }

      // Delay between downloads so the browser processes each one
      if (i < total - 1) {
        await new Promise(resolve => setTimeout(resolve, 2500));
      }
    }

    setIsGenerating(false);

    if (failed === total) {
      setToastType('error');
    } else {
      setToastType('success');
      setFilename(`${total - failed} FEMA report(s)`);
    }

    setProgress({ current: total, total, failed });

    autoDismissTimerRef.current = setTimeout(() => {
      dismissToast();
    }, 20_000);
  }, [dismissToast]);

  // ── Return shape ───────────────────────────────────────────────────────────
  return {
    isGenerating,
    toastVisible,
    toastType,
    blobUrl,
    filename,
    progress,
    generate,
    generateMultiple,
    dismissToast,
  };
}

export default useFEMAReport;
